import { DownOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import AISparkle from "components/ai/AISparkle";
import { ImportGlyph } from "components/ai/glyphs";
import "styles/ai_components.css";

export const AI_TOOLS_PATH = "/tenant/ai";

const FOLD_KEY = "qai-nav-open";

const readFold = () => {
  try {
    return window.localStorage.getItem(FOLD_KEY) !== "false";
  } catch {
    return true;
  }
};

const writeFold = (open: boolean) => {
  try {
    window.localStorage.setItem(FOLD_KEY, String(open));
  } catch {
    /* private window or blocked site data */
  }
};

const activeHere = () =>
  typeof window === "undefined" || !window.location.pathname.startsWith(AI_TOOLS_PATH)
    ? null
    : new URLSearchParams(window.location.search);

const GROUPS = [
  { tool: "create", entities: ["products", "accounts", "categories"] },
  { tool: "import", entities: ["products", "accounts", "contacts"] },
] as const;

export default function AiNavEntry({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(readFold);
  const here = activeHere();
  const title = t("onboarding_wizard.ai_tools.nav.title");

  if (collapsed) {
    return (
      <Tooltip title={title} placement="right">
        <a className="qai-nav qai-nav--collapsed" href={AI_TOOLS_PATH} aria-label={title}>
          <AISparkle variant="double" tone="inverse" size={18} />
        </a>
      </Tooltip>
    );
  }

  return (
    <div className="qai-nav" data-open={open}>
      <button
        type="button"
        className="qai-nav__head"
        aria-expanded={open}
        aria-controls="qai-nav-list"
        onClick={() =>
          setOpen((prev) => {
            writeFold(!prev);
            return !prev;
          })
        }
      >
        <AISparkle variant="double" tone="inverse" size={18} />
        <span className="qai-nav__txt">
          <span className="qai-nav__label">{title}</span>
          <span className="qai-nav__sub">{t("onboarding_wizard.ai_tools.nav.subtitle")}</span>
        </span>
        <DownOutlined className="qai-nav__caret" />
      </button>
      <div className="qai-nav__list" id="qai-nav-list" hidden={!open}>
        {GROUPS.map(({ tool, entities }) => (
          <div className="qai-nav__group" key={tool}>
            <span className="qai-nav__group-head">
              {tool === "create" ? (
                <AISparkle variant="single" size={16} />
              ) : (
                <ImportGlyph size={16} />
              )}
              {t(`onboarding_wizard.ai_tools.nav.${tool}`)}
            </span>
            {entities.map((entity) => (
              <a
                key={entity}
                href={`${AI_TOOLS_PATH}?tool=${tool}&entity=${entity}`}
                className={
                  here?.get("tool") === tool && here.get("entity") === entity
                    ? "qai-nav__leaf is-active"
                    : "qai-nav__leaf"
                }
              >
                <span className="qai-nav__dot" aria-hidden="true" />
                {t(`onboarding_wizard.ai_tools.nav.${tool}_${entity}`)}
              </a>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
